import { pixelDrawingBoundsSchema } from 'features/libra/drawing/drawingBounds'
import { zoneParamsSchema } from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import { z } from 'zod'

import { workPackConfigSchema } from './pdf/workPackTypes'
import { methodAssignmentSchema, methodPlanningSchema } from './techniqueTypes'

export const planningSourceCaptureSchema = z.object({
  sourceField: z.string(),
  sourceZonesField: z.string(),
  copyFields: z.record(z.string(), z.string()),
  sourceContextField: z.string(),
})

export type PlanningSourceCapture = z.infer<typeof planningSourceCaptureSchema>

export const niiAssessmentReferenceSchema = z.object({
  ticketId: z.string(),
  title: z.string(),
  version: z.number(),
  workflowId: z.string(),
  capturedAt: z.string(),
})

export type NiiAssessmentReference = z.infer<
  typeof niiAssessmentReferenceSchema
>
export const sourceZonesSchema = z
  .array(zoneParamsSchema)
  .transform((zones) => zones.filter((zone) => !zone.ai_suggested))

export const inspectionAreaSchema = z.object({
  id: z.string(),
  sourceZoneId: z.string(),
  title: z.string(),
  scope: z.enum(['location', 'zone']).optional(),
  zone_reference_image_id: z.string().optional(),
  drawing_bounds: pixelDrawingBoundsSchema.optional(),
  degradationTypeId: z.string().optional(),
  methodAssignments: z.array(methodAssignmentSchema),
})

export type InspectionArea = z.infer<typeof inspectionAreaSchema>
export type InspectionAreaDrawing = Required<
  Pick<InspectionArea, 'drawing_bounds' | 'zone_reference_image_id'>
>
export type InspectionAreaUpdate = Partial<
  Omit<InspectionArea, 'id' | 'sourceZoneId'>
>

const strategyConfigSchema = z.object({
  detailedDrawing: z.boolean(),
  requirePlan: z.boolean().optional(),
  guidance: z.string().optional(),
  plannedCoveragePercent: z.number().min(0).max(100).optional(),
})

export const planningStrategiesSchema = z.object({
  A: strategyConfigSchema,
  B: strategyConfigSchema,
  C: strategyConfigSchema,
})
export type PlanningStrategies = z.infer<typeof planningStrategiesSchema>

export const planningAreasConfigSchema = z.object({
  workPack: workPackConfigSchema.optional(),
  sourceZonesField: z.string(),
  areasField: z.string(),
  recommendationsField: z.string(),
  techniqueCatalogueContextKey: z.string(),
  methodPlanning: methodPlanningSchema,
  quantities: z
    .object({
      warnBelowTarget: z.boolean(),
      warnAboveTarget: z.boolean(),
    })
    .optional(),
})

export type PlanningAreasConfig = z.infer<typeof planningAreasConfigSchema>
